import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

@Component({
	selector: 'app-daily-usage-charts',
	templateUrl: './daily-usage-charts.component.html',
	styleUrls: ['./daily-usage-charts.component.css']
})
export class DailyUsageChartsComponent implements OnChanges{
	@Input() data: any;
	@Input() selectedDays: 'allDays' | 'workingDays' = 'allDays';
	@Input() onPrint: boolean = false;

	// In-Use Ah chart
	inUseAhData: any[] = [];
	inUseAhColumns = ['Date', 'In-Use Ah'];
	inUseAhOptions = {
		colors: ['#8B2500'],
		legend: { position: 'top', alignment: 'center' },
		bar: { groupWidth: '70%' },
		vAxis: { title: 'In-Use Ah' },
		hAxis: { 
			title: 'Date', 
			slantedText: true, 
			slantedTextAngle: 45,
			textStyle: { fontSize: 10 }
		},
		chartArea: { width: '80%', height: '60%', bottom: 80 }, 
		animation: { startup: true, duration: 500, easing: 'out' },
	};

	// UBU chart
	ubuData: any[] = [];
	ubuColumns = ['Date', 'In-Use UBU'];
	ubuOptions = {
		colors: ['#CC0000'],
		legend: { position: 'top', alignment: 'center' },
		bar: { groupWidth: '70%' },
		vAxis: { title: 'In Use UBU' },
		hAxis: { 
			title: 'Date', 
			slantedText: true, 
			slantedTextAngle: 45,
			textStyle: { fontSize: 10 }
		},
		chartArea: { width: '80%', height: '60%', bottom: 80 },
		animation: { startup: true, duration: 500, easing: 'out' },
	};

	// Chart 3 - Durations
	durationsData: any[] = [];
	durationsColumns = [
		'Date',
		'In-Use Hours',   { type: 'string', role: 'tooltip', p: { html: true } },
		'Idle/Off Hours', { type: 'string', role: 'tooltip', p: { html: true } },
		'Charge Hours',   { type: 'string', role: 'tooltip', p: { html: true } },
	];

	durationsOptions = {
		colors: ['#CC0000', '#AAAAAA', '#1A3A6B'],
		legend: { position: 'top', alignment: 'center' },
		bar: { groupWidth: '75%' },
		isStacked: false,
		vAxis: { 
			title: 'Hours',
			ticks: [0, 2, 4, 6, 8, 10, 12, 16, 20, 24].map(h => ({
				v: h,
				f: `${h}:00`
			}))
		},
		hAxis: { 
			title: 'Date', 
			slantedText: true, 
			slantedTextAngle: 45,
			textStyle: { fontSize: 10 }
		},
		chartArea: { width: '80%', height: '60%', bottom: 80 },
		animation: { startup: true, duration: 500, easing: 'out' },
		tooltip: { isHtml: true },
	};

	// Chart 4 - Charge Opportunities
	chargeOppData: any[] = [];
	chargeOppColumns = ['Date', 'Daily Charge Opportunities', { type: 'string', role: 'tooltip', p: { html: true } },];
	chargeOppOptions = {
		colors: ['#1A6B8A'],
		legend: { position: 'top', alignment: 'center' },
		bar: { groupWidth: '70%' },
		vAxis: { 
			title: 'Charge Opportunities - Hours',
			ticks: [0, 2, 4, 6, 8, 10, 12, 16, 20, 24].map(h => ({
				v: h,
				f: `${h}:00`
			}))
		},
		hAxis: { 
			title: 'Date', 
			slantedText: true, 
			slantedTextAngle: 45,
			textStyle: { fontSize: 10 }
		},
		chartArea: { width: '80%', height: '60%', bottom: 80 },
		animation: { startup: true, duration: 500, easing: 'out' },
		tooltip: { isHtml: true },
	};

	readyStates: { [key: string]: boolean } = {};


	ngOnChanges(changes: SimpleChanges) {
		if (!this.data) {
			this.inUseAhData   = [];
			this.ubuData       = [];
			this.durationsData = [];
			this.chargeOppData = [];
			return;
		}

		if ((changes['data'] || changes['selectedDays']) && this.data) {
			this.buildCharts();
		}
	}

	private buildCharts(): void {
		const dayType = this.selectedDays || 'allDays';
		const chartData = this.data?.[dayType]?.dailyChartData;

		if (!chartData || !chartData.length) {
			this.inUseAhData   = [];
			this.ubuData       = [];
			this.durationsData = [];
			this.chargeOppData = [];
			return;
		}

		this.inUseAhData   = chartData.map(row => [row[0], row[1]]);
		this.ubuData       = chartData.map(row => [row[0], row[2]]);
		this.durationsData = chartData.map(row => [
			row[0],
			row[3], `<div style="padding:8px"><b>${row[0]}</b><br>In-Use Hours: <b>${this.toHHMM(row[3])}</b></div>`,
			row[4], `<div style="padding:8px"><b>${row[0]}</b><br>Idle/Off Hours: <b>${this.toHHMM(row[4])}</b></div>`,
			row[5], `<div style="padding:8px"><b>${row[0]}</b><br>Charge Hours: <b>${this.toHHMM(row[5])}</b></div>`,
		]);
		this.chargeOppData = chartData.map(row => [
			row[0], 
			row[6], `<div style="padding:8px"><b>${row[0]}</b><br>Daily Charge Opportunities: <b>${this.toHHMM(row[6])}</b></div>`,
		]);
	}

	toHHMM(decimalHrs: number): string {
		const totalMins = Math.round(decimalHrs * 60);
		const h = Math.floor(totalMins / 60);
		const mm = String(totalMins % 60).padStart(2, '0');
		return `${h}:${mm}`;
	}

	onChartReady([id, state]: [string, boolean]): void {
		this.readyStates[id] = state;
	}

	isReady(id: string): boolean {
		return !!this.readyStates[id];
	}
}
